import { ColumnDef } from "@tanstack/react-table";
import { Dispatch, memo, SetStateAction, useMemo } from "react";
import { Button } from "../../button";
import { ArrowUpDown } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import { Toaster } from "react-hot-toast";
import {
  EVACUATION_EVENT_PARTICIPANTS_STATUSES,
  EVACUATION_METHODS,
  TABLE_IDS,
} from "../../../constants/common";
import { FRAccessLog } from "../../../hooks/report/interface";
import { cn } from "../../../utils/cn";
import { EvacuationEventParticipant } from "../../../hooks/emergency-evacuation/interface";
import { formattedDateTimeWithoutSec } from "../../../lib/functions";

interface EvacuationReportTableProps {
  allEvacuationReports: FRAccessLog[];
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const EvacuationReportTable = ({
  allEvacuationReports,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  pageSize,
  setPageSize,
}: EvacuationReportTableProps) => {
  const { i18n, t } = useTranslation();

  const columns: ColumnDef<EvacuationEventParticipant>[] = useMemo(
    () => [
      {
        accessorKey: "s_EvacuationCode",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("evacuationCode")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_EvacuationStatus_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("status")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <div
              className={cn(
                "text-center rounded-lg py-0.5 px-1",
                row.original.n_EvacuationStatus ===
                  EVACUATION_EVENT_PARTICIPANTS_STATUSES.MISSING
                  ? "bg-red-500"
                  : row.original.n_EvacuationStatus ===
                      EVACUATION_EVENT_PARTICIPANTS_STATUSES.CANCELLED
                    ? "bg-orange-600"
                    : row.original.n_EvacuationStatus ===
                        EVACUATION_EVENT_PARTICIPANTS_STATUSES.SAFE
                      ? "bg-green-600"
                      : "bg-blue-600",
              )}
            >
              <p className="text-white">
                {i18n.language === "en"
                  ? row.original.s_EvacuationStatus_En
                  : row.original.s_EvacuationStatus_Ar}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "s_FullName",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("name")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_Sponsor",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("companyName")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_MobileNo",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("phoneNumber")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_Email",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("email")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_EventType_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("eventType")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <p>
            {i18n.language === "en"
              ? row.original.s_EventType_En
              : row.original.s_EventType_Ar}
          </p>
        ),
      },
      {
        accessorKey: "s_PersonType_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("userType")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <p>
            {i18n.language === "en"
              ? row.original.s_PersonType_En
              : row.original.s_PersonType_Ar}
          </p>
        ),
      },
      {
        accessorKey: "s_MusterPointName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("musterPoint")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <p>
              {i18n.language === "en"
                ? row.original.s_MusterPointName_En
                : row.original.s_MusterPointName_Ar}
            </p>
          );
        },
      },
      {
        accessorKey: "s_ExpectedLocation_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("lastSeen")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <div>
            <p>
              {t("location")}:{" "}
              {i18n.language === "en"
                ? row.original.s_ExpectedLocation_En
                : row.original.s_ExpectedLocation_Ar}
            </p>
            <p>
              {t("building")}:{" "}
              {i18n.language === "en"
                ? row.original.s_ExpectedBuilding_En
                : row.original.s_ExpectedBuilding_Ar}
            </p>
          </div>
        ),
      },
      {
        accessorKey: "n_EvacuationMethod",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("method")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          if (!row.original.n_EvacuationMethod) return;

          return (
            <div
              className={cn(
                "text-center rounded-lg py-0.5 px-1",
                row.original.n_EvacuationMethod === EVACUATION_METHODS.MANUAL
                  ? "bg-blue-600"
                  : row.original.n_EvacuationMethod === EVACUATION_METHODS.QR
                    ? "bg-orange-600"
                    : row.original.n_EvacuationMethod ===
                        EVACUATION_METHODS.FR_READER
                      ? "bg-pink-600"
                      : "bg-blue-600",
              )}
            >
              <p className="text-white">
                {i18n.language === "en"
                  ? row.original.s_EvacuationMethod_En
                  : row.original.s_EvacuationMethod_Ar}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "s_ActionByName",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("actionBy")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "dt_ActionDateTime",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("actionDate")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          if (!row.original.dt_ActionDateTime) return;

          return (
            <p>{formattedDateTimeWithoutSec(row.original.dt_ActionDateTime)}</p>
          );
        },
      },
    ],
    [i18n.language, t],
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <TableWrapper
        data={allEvacuationReports}
        columns={columns}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isSearch
        tableId={TABLE_IDS.EVACUATION_REPORT}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
    </>
  );
};

export default memo(EvacuationReportTable);
